import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import RoleSelector from './components/RoleSelector';
import SupplierFields from './components/SupplierFields';
import PasswordStrengthIndicator from './components/PasswordStrengthIndicator';
import CountryCurrencySelector from './components/CountryCurrencySelector';
import RegistrationSuccess from './components/RegistrationSuccess';
import { authService } from '../../services/authService';

const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [requiresEmailConfirmation, setRequiresEmailConfirmation] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: '',
    businessName: '',
    serviceDescription: '',
    profile_image: '',
    country: 'ie',
    currency: 'eur',
    // Addresses: array of objects { label, locality, city, state, pinCode, country, isPrimary }
    addresses: [],
    agreeToTerms: false,
    agreeToPrivacy: false
  });

  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+?[\d\s-()]{10,}$/;
    return phoneRegex?.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData?.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select your role';
    }

    if (formData?.role === 'supplier') {
      if (!formData?.businessName?.trim()) {
        newErrors.businessName = 'Business name is required';
      }
      if (!formData?.serviceDescription?.trim()) {
        newErrors.serviceDescription = 'Service description is required';
      } else if (formData?.serviceDescription?.trim()?.length < 50) {
        newErrors.serviceDescription = 'Description must be at least 50 characters';
      }
    }

    if (!formData?.country) {
      newErrors.country = 'Please select your country';
    }

    if (!formData?.currency) {
      newErrors.currency = 'Please select your preferred currency';
    }

    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the Terms of Service';
    }

    if (!formData?.agreeToPrivacy) {
      newErrors.agreeToPrivacy = 'You must agree to the Privacy Policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      role,
      // initialize address slots depending on role
      addresses: role === 'customer' ? [
        { label: 'Home', locality: '', city: '', state: '', pinCode: '', country: prev?.country || 'us', isPrimary: true },
        { label: 'Work', locality: '', city: '', state: '', pinCode: '', country: prev?.country || 'us', isPrimary: false }
      ] : role === 'supplier' ? [
        { label: 'Work', locality: '', city: '', state: '', pinCode: '', country: prev?.country || 'us', isPrimary: true }
      ] : []
    }));
    if (errors?.role) {
      setErrors(prev => ({
        ...prev,
        role: ''
      }));
    }
  };

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => {
      const next = { ...prev };
      next.addresses = Array.isArray(next.addresses) ? [...next.addresses] : [];
      next.addresses[index] = { ...(next.addresses[index] || {}), [field]: value };
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      const firstError = Object.keys(errors)?.[0];
      const errorElement = document.querySelector(`[name="${firstError}"]`);
      if (errorElement) {
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({}); // Clear any previous errors

    try {
      const userData = {
        fullName: formData?.fullName,
        phone: formData?.phone,
        role: formData?.role,
        businessName: formData?.businessName,
        serviceDescription: formData?.serviceDescription,
        country: formData?.country,
        currency: formData?.currency,
        addresses: Array.isArray(formData?.addresses) ? formData?.addresses : [],
        profile_image: formData?.profile_image || ''
      };

      console.log('Submitting registration for:', formData?.email);
      const { data, error } = await authService?.signUp(formData?.email, formData?.password, userData);

      if (error) {
        console.error('Registration error:', error);
        let errorMessage = error?.message || 'Registration failed. Please try again.';
        
        // Handle Supabase rate limit errors
        if (error?.code === 'over_email_send_rate_limit' || error?.message?.toLowerCase()?.includes('rate limit')) {
          errorMessage = 'Too many registration attempts. Supabase has temporarily limited email sending to this address. Please wait 1 hour before trying again, or try a different email address.';
        }
        // Provide more specific error messages
        else if (error?.message?.includes('already registered')) {
          errorMessage = 'This email is already registered. Please try logging in instead.';
        } else if (error?.message?.includes('invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        } else if (error?.message?.includes('password')) {
          errorMessage = 'Password must be at least 8 characters long.';
        }
        
        setErrors({ submit: errorMessage });
        setIsSubmitting(false);
        return;
      }

      // Verify we got user data back
      if (!data?.user) {
        console.error('No user data returned');
        setErrors({ submit: 'Registration failed: Unable to create account. Please try again or contact support.' });
        setIsSubmitting(false);
        return;
      }

      // Registration successful
      console.log('Registration successful for:', formData?.email);
      setRegisteredEmail(formData?.email);
      setRequiresEmailConfirmation(data?.requiresEmailConfirmation || false);
      setRegistrationComplete(true);
    } catch (err) {
      console.error('Unexpected registration error:', err);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
      setIsSubmitting(false);
    }
  };

  if (registrationComplete) {
    return <RegistrationSuccess email={registeredEmail} requiresEmailConfirmation={requiresEmailConfirmation} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-3 mb-6 transition-smooth hover:opacity-80"
          >
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Icon name="ChefHat" size={28} color="var(--color-primary)" />
            </div>
            <span className="text-2xl font-heading font-bold text-foreground">
              HungryElph
            </span>
          </button>

          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
            Create Your Account
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            Join our community of home chefs and food lovers
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-warm-lg p-6 sm:p-8 lg:p-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <RoleSelector
              selectedRole={formData?.role}
              onRoleChange={handleRoleChange}
            />
            {errors?.role && (
              <p className="text-sm text-error -mt-2">{errors?.role}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <Input
                label="Full Name"
                type="text"
                name="fullName"
                placeholder="John Doe"
                value={formData?.fullName}
                onChange={handleChange}
                error={errors?.fullName}
                required
              />

              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="john@example.com"
                value={formData?.email}
                onChange={handleChange}
                error={errors?.email}
                required
              />
            </div>

            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              placeholder="+353 89 1234567"
              value={formData?.phone}
              onChange={handleChange}
              error={errors?.phone}
              required
              description="Include country code for international numbers"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <div className="relative">
                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Create a strong password"
                    value={formData?.password}
                    onChange={handleChange}
                    error={errors?.password}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[38px] p-1 hover:bg-muted rounded transition-smooth"
                  >
                    <Icon
                      name={showPassword ? 'EyeOff' : 'Eye'}
                      size={18}
                      color="var(--color-muted-foreground)"
                    />
                  </button>
                </div>
                <PasswordStrengthIndicator password={formData?.password} />
              </div>

              <div className="relative">
                <Input
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Re-enter your password"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  error={errors?.confirmPassword}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-[38px] p-1 hover:bg-muted rounded transition-smooth"
                >
                  <Icon
                    name={showConfirmPassword ? 'EyeOff' : 'Eye'}
                    size={18}
                    color="var(--color-muted-foreground)"
                  />
                </button>
              </div>
            </div>

            {formData?.role === 'supplier' && (
              <SupplierFields
                formData={formData}
                errors={errors}
                onChange={handleChange}
              />
            )}

            {/* Address inputs (conditionally displayed for customer/supplier) */}
            {(formData?.role === 'customer' || formData?.role === 'supplier') && (
              <div className="mt-4">
                <h3 className="font-heading font-semibold text-lg mb-3">Add Address</h3>
                <p className="text-sm text-muted-foreground mb-4">You can add a {formData?.role === 'customer' ? 'Home and Work' : 'Work'} address now. You can update these later in your profile.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Array.isArray(formData?.addresses) && formData.addresses.map((addr, idx) => (
                    <div key={idx} className="bg-muted/5 p-4 rounded-lg">
                      <Input label="Label" name={`addr-label-${idx}`} value={addr?.label || ''} onChange={(e) => handleAddressChange(idx, 'label', e?.target?.value)} />
                      <Input label="Locality / Street" name={`addr-locality-${idx}`} value={addr?.locality || ''} onChange={(e) => handleAddressChange(idx, 'locality', e?.target?.value)} />
                      <Input label="City" name={`addr-city-${idx}`} value={addr?.city || ''} onChange={(e) => handleAddressChange(idx, 'city', e?.target?.value)} />
                      <Input label="State" name={`addr-state-${idx}`} value={addr?.state || ''} onChange={(e) => handleAddressChange(idx, 'state', e?.target?.value)} />
                      <Input label="Pin / Zip Code" name={`addr-pin-${idx}`} value={addr?.pinCode || ''} onChange={(e) => handleAddressChange(idx, 'pinCode', e?.target?.value)} />
                      <div className="mt-2">
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" checked={addr?.isPrimary} onChange={(e) => handleAddressChange(idx, 'isPrimary', !!e?.target?.checked)} />
                          <span className="text-sm">Set as primary</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <CountryCurrencySelector
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <div className="space-y-3 pt-4 border-t border-border">
              <Checkbox
                label={
                  <span className="text-sm">
                    I agree to the{' '}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={(e) => {
                        e?.preventDefault();
                        window.open('/terms', '_blank');
                      }}
                    >
                      Terms of Service
                    </button>
                  </span>
                }
                name="agreeToTerms"
                checked={formData?.agreeToTerms}
                onChange={handleChange}
                error={errors?.agreeToTerms}
              />

              <Checkbox
                label={
                  <span className="text-sm">
                    I agree to the{' '}
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={(e) => {
                        e?.preventDefault();
                        window.open('/privacy', '_blank');
                      }}
                    >
                      Privacy Policy
                    </button>
                  </span>
                }
                name="agreeToPrivacy"
                checked={formData?.agreeToPrivacy}
                onChange={handleChange}
                error={errors?.agreeToPrivacy}
              />
            </div>

            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <Icon name="Shield" size={20} color="var(--color-success)" />
              <p className="text-sm text-muted-foreground">
                Your data is encrypted and secure. We never share your information.
              </p>
            </div>

            {errors?.submit && (
              <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/20 rounded-lg">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isSubmitting}
              iconName="UserPlus"
              iconPosition="right"
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>

            <div className="text-center pt-4">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-primary font-medium hover:underline transition-smooth"
                >
                  Sign In
                </button>
              </p>
            </div>
          </form>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            By creating an account, you agree to receive promotional emails and updates.
            <br />
            You can unsubscribe at any time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;